import { cn } from "@istari-frontend/shared";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import { StatusCard } from "~/components/status-card";
import { useJsonTreeLines } from "~/components/use-json-tree-lines";
import { ThemedLoadingSpinner } from "~/theming/feature/themed-loading-spinner";
import { buildJsonPath } from "~/utils/jsonpath";

import { SelectionAction } from "./file-viewer/selection-action";
import {
  collectAllExpandableIds,
  formatJsonAsTreeViewerText,
  formatLeafValue,
  getIndent,
  type JsonLine,
  type JsonTreeSelection,
  type JsonValue,
} from "./json-tree-viewer.utils";

export { formatJsonAsTreeViewerText };

interface JsonTreeViewerProps {
  data: JsonValue;
  onSelectionChange?: (action: SelectionAction) => void;
  selection?: JsonTreeSelection;
  fallback?: () => React.ReactNode;
}

function useJsonTreeSelection(lines: JsonLine[] | undefined, onSelectionChange?: (action: SelectionAction) => void) {
  const anchorRef = useRef<number | null>(null);

  const commitSelection = useCallback(
    (next: { start: number; end: number } | null) => {
      if (!onSelectionChange) return;

      if (next) {
        const startLine = Math.max(1, Math.min(next.start, next.end));
        const endLine = Math.min(lines?.length ?? 0, Math.max(next.start, next.end));
        onSelectionChange({ type: "line-range", startLine, endLine });
      } else {
        onSelectionChange({ type: "clear" });
      }
    },
    [lines?.length, onSelectionChange],
  );

  const beginSelection = useCallback(
    (lineNumber: number, isShift: boolean, _currentSelection: JsonTreeSelection | undefined) => {
      if (isShift && anchorRef.current !== null) {
        const start = Math.min(anchorRef.current, lineNumber);
        const end = Math.max(anchorRef.current, lineNumber);
        commitSelection({ start, end });
      } else {
        anchorRef.current = lineNumber;
        commitSelection({ start: lineNumber, end: lineNumber });
      }
    },
    [commitSelection],
  );

  return { beginSelection };
}

function useJsonTree(
  data: JsonValue,
  onSelectionChange?: (action: SelectionAction) => void,
  externalSelection?: JsonTreeSelection,
) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [selection, setSelection] = useState<JsonTreeSelection>(externalSelection ?? { type: "none" });

  useEffect(() => {
    setExpandedIds(collectAllExpandableIds(data));
  }, [data]);

  useEffect(() => {
    const newSelection = externalSelection ?? { type: "none" };
    setSelection((prevSelection) => {
      if (JSON.stringify(prevSelection) !== JSON.stringify(newSelection)) {
        return newSelection;
      }
      return prevSelection;
    });
  }, [externalSelection]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const jsonTreeLinesState = useJsonTreeLines(data, expandedIds);

  const { beginSelection: beginLineSelection } = useJsonTreeSelection(
    jsonTreeLinesState.status === "success" ? jsonTreeLinesState.value.lines : undefined,
    onSelectionChange,
  );

  const beginSelection = useCallback(
    (lineNumber: number, isShift: boolean) => {
      beginLineSelection(lineNumber, isShift, selection);
    },
    [beginLineSelection, selection],
  );

  const handleLeafClick = useCallback(
    (path: (string | number)[]) => {
      if (!onSelectionChange) return;
      const jsonPath = buildJsonPath(path);
      onSelectionChange({ type: "json-path", path: jsonPath });
    },
    [onSelectionChange],
  );

  return jsonTreeLinesState.status === "success"
    ? {
        status: "success" as const,
        visibleLinesWithNumbers: jsonTreeLinesState.value.visibleLinesWithNumbers,
        digits: jsonTreeLinesState.value.digits,
        expandedIds,
        toggleExpand,
        selection,
        beginSelection,
        handleLeafClick,
      }
    : jsonTreeLinesState;
}

function JsonTreeLine({
  children,
  line,
  isSelected,
  isFirstSelected,
  isLastSelected,
  digits,
}: {
  children: React.ReactNode;
  line: JsonLine;
  isSelected: boolean;
  isFirstSelected: boolean;
  isLastSelected: boolean;
  digits: number;
}) {
  return (
    <div
      key={`${line.id}`}
      className={cn(
        "relative grid pl-2",
        isSelected && "bg-primary/[0.02]",
        isFirstSelected &&
          "before:bg-primary/20 before:absolute before:top-0 before:right-0 before:left-0 before:z-20 before:h-[0.5px]",
        isLastSelected &&
          "after:bg-primary/20 after:absolute after:right-0 after:bottom-0 after:left-0 after:z-20 after:h-[0.5px]",
      )}
      style={{ gridTemplateColumns: `${digits}ch 3ch 1fr` }}
    >
      {children}
    </div>
  );
}

function LineNumberCell({
  lineNumber,
  digits,
  onMouseDown,
}: {
  lineNumber: number;
  digits: number;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      className="bg-card text-muted-foreground hover:bg-muted hover:text-foreground sticky left-0 z-10 cursor-default border border-transparent text-right tabular-nums select-none"
      style={{ width: `${digits}ch` }}
      onMouseDown={onMouseDown}
    >
      {lineNumber}
    </div>
  );
}

function ChevronCell({
  line,
  digits,
  onToggle,
  onMouseDown,
}: {
  line: JsonLine;
  digits: number;
  onToggle: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  const showChevron = line.isExpandable && (line.type === "opening" || line.type === "property");

  return (
    <div
      className="text-muted-foreground bg-card sticky z-10 flex items-center justify-center"
      style={{ left: `${digits}ch` }}
      onMouseDown={onMouseDown}
    >
      {showChevron && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onToggle();
            }
          }}
          className="hover:bg-muted/50 focus:ring-ring inline-flex items-center justify-center rounded-xs focus:outline-none focus-visible:ring-2"
          tabIndex={0}
          aria-label={line.isExpanded ? "Collapse" : "Expand"}
        >
          {line.isExpanded ? (
            <ChevronDown className="h-3 w-3 shrink-0" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0" />
          )}
        </button>
      )}
    </div>
  );
}

function ContentCell({ line, children }: { line: JsonLine; children: React.ReactNode }) {
  return (
    <div className="w-full min-w-0 pr-1.5 whitespace-nowrap">
      <span className="text-foreground">
        {getIndent(line.depth)}
        {children}
      </span>
    </div>
  );
}

function LeafValueButton({
  line,
  onLeafClick,
  isSelected,
}: {
  line: JsonLine;
  onLeafClick: (path: (string | number)[]) => void;
  isSelected: boolean;
}) {
  if (!line.value) return null;

  return (
    <>
      {line.key}
      {": "}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onLeafClick(line.path);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onLeafClick(line.path);
          }
        }}
        className={cn(
          "inline-flex items-center rounded-xs py-0 font-mono text-xs select-text",
          "focus:ring-ring focus:outline-none focus-visible:ring-2",
          "transition-all duration-75",
          isSelected
            ? "border-primary bg-primary/20 hover:bg-primary/25 active:bg-primary/30 border"
            : "bg-primary/10 hover:bg-primary/20 active:bg-primary/30 border border-transparent",
          "active:scale-98",
        )}
        tabIndex={0}
      >
        {formatLeafValue(line.value)}
      </button>
    </>
  );
}

function CollapsedIndicator({ line, onToggle }: { line: JsonLine; onToggle: () => void }) {
  return (
    <>
      {line.content.replace(/\.\.\.\]$|\.\.\.\}$/, "")}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onToggle();
          }
        }}
        className={cn(
          "inline-flex items-center rounded-xs border border-transparent py-0 font-mono text-xs",
          "focus:ring-ring focus:outline-none focus-visible:ring-2",
          "bg-primary/10 hover:bg-primary/20 active:bg-primary/30 active:scale-98",
          "transition-all duration-30 ease-out",
        )}
        tabIndex={0}
        aria-label="Expand"
      >
        ...
      </button>
      {line.content.match(/\.\.\.\]$/) ? "]" : "}"}
    </>
  );
}

/**
 * @description
 * This component is used to display a JSON tree in a tree view.
 *
 * @todo [PROD-961] Selection highlight background does not extend full width of container - the grid rows only extend as wide as their content, causing the bg-primary/[0.02] background to be cut short even though parent has overflow-auto
 */
export function JsonTreeViewer({
  data,
  onSelectionChange,
  selection: externalSelection,
  fallback,
}: JsonTreeViewerProps) {
  const treeState = useJsonTree(data, onSelectionChange, externalSelection);

  if (treeState.status === "error") {
    return (
      fallback?.() ?? (
        <StatusCard
          icon={<AlertTriangle />}
          title="Error loading JSON tree"
          description={`The JSON file could not be parsed. Please check the file contents and try again.}`}
        />
      )
    );
  }

  if (treeState.status === "loading") {
    return (
      <StatusCard
        icon={<ThemedLoadingSpinner width="24px" height="24px" />}
        title="Loading file"
        description="Generating JSON tree..."
      />
    );
  }

  return (
    <div className="bg-background h-full w-full overflow-auto py-2">
      <div className="flex h-full flex-col font-mono text-xs">
        <Virtuoso
          data={treeState.visibleLinesWithNumbers}
          itemContent={(_, { line, lineNumber }) => {
            const isLineRangeSelected =
              treeState.selection.type === "line-range" &&
              lineNumber >= treeState.selection.startLine &&
              lineNumber <= treeState.selection.endLine;
            const isFirstSelected =
              treeState.selection.type === "line-range" && lineNumber === treeState.selection.startLine;
            const isLastSelected =
              treeState.selection.type === "line-range" && lineNumber === treeState.selection.endLine;
            const isLeafLine = !line.isExpandable && line.type === "property";
            const isLeafSelected =
              treeState.selection.type === "json-path" && treeState.selection.path === buildJsonPath(line.path);

            return (
              <JsonTreeLine
                key={`${line.id}-${lineNumber}`}
                line={line}
                isSelected={isLineRangeSelected}
                isFirstSelected={isFirstSelected}
                isLastSelected={isLastSelected}
                digits={treeState.digits}
              >
                <LineNumberCell
                  lineNumber={lineNumber}
                  digits={treeState.digits}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    treeState.beginSelection(lineNumber, e.shiftKey);
                  }}
                />

                <ChevronCell
                  line={line}
                  digits={treeState.digits}
                  onToggle={() => treeState.toggleExpand(line.id)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    treeState.beginSelection(lineNumber, e.shiftKey);
                  }}
                />

                <ContentCell line={line}>
                  {isLeafLine && onSelectionChange && line.key && line.value ? (
                    <LeafValueButton line={line} onLeafClick={treeState.handleLeafClick} isSelected={isLeafSelected} />
                  ) : line.isExpandable && !line.isExpanded ? (
                    <CollapsedIndicator line={line} onToggle={() => treeState.toggleExpand(line.id)} />
                  ) : (
                    line.content
                  )}
                </ContentCell>
              </JsonTreeLine>
            );
          }}
          defaultItemHeight={18}
          increaseViewportBy={180}
        />
      </div>
    </div>
  );
}
